import { useEffect } from 'react';
import urltemplate from 'url-template';
import type {
  PortfolioCodesParams,
  PortfolioCodesResponse,
} from 'src/apis/transport-auction/types';
import type { OptionalTransportAuctionServiceRootRequest } from 'src/apis/transport-auction/use-transport-auction-service-root';
import { useAxios } from 'src/hooks/use-axios';

type HookOptions = {
  root: OptionalTransportAuctionServiceRootRequest;
  params: PortfolioCodesParams;
  /**
   * Bundled network points are currently ignored as we do not have the UUID per side
   * in the old API payload.
   */
  bundled: boolean;
};

type ExecuteOptions = {
  url: string;
  params: PortfolioCodesParams;
};

export function useAssignedPortfolioCodes({
  params,
  root,
  bundled,
}: HookOptions) {
  const portfolioCodes = useAxios(
    (axios, baseConfig, { url, params }: ExecuteOptions) =>
      axios.request<PortfolioCodesResponse>({
        ...baseConfig,
        // use templated urls here instead of using the params field,
        // as they are marked as required params in the URL template
        url: urltemplate.parse(url).expand(params),
        // params,
      }),
    { neededOnPageLoad: true }
  );

  const assignedPortfolioCodesLink =
    root?.response?.data._links.assignedPortfolioCodes;

  useEffect(() => {
    if (!assignedPortfolioCodesLink) return;
    if (bundled) return;

    portfolioCodes.execute({
      url: assignedPortfolioCodesLink.href,
      params,
    });
    // oxlint-disable-next-line exhaustive-deps
  }, [assignedPortfolioCodesLink, bundled, params]);

  if (!assignedPortfolioCodesLink) return;
  if (bundled) return;

  return portfolioCodes;
}

export type AssignedPortfolioCodesRequest = ReturnType<
  typeof useAssignedPortfolioCodes
>;
